import { useCallback, useEffect } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAppData } from '../app/AppContext';
import { Dialog } from '../shared/ui/Dialog';
import { HistoryPage, ReportsPage } from './pages';
import { activityPath, parseActivityState, useActivityState, workspaceToday } from './activity-state';
import { formatReportPeriodTitle, shiftReportPeriodAnchor } from './report-format';
import './activity.css';

export function LegacyActivityRedirect({ mode }: { mode: 'history' | 'reports' }) {
  const { workspace } = useAppData();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('mode', mode);
  const overlay = params.get('overlay');
  return <Navigate replace to={activityPath(parseActivityState(params, workspaceToday(workspace?.timezone ?? 'Europe/Moscow')), overlay === 'history-months' ? 'report-period' : overlay)} state={location.state} />;
}

export function ActivityPage() {
  const activity = useActivityState();
  const { period, anchor, setState } = activity;
  const [params] = useSearchParams();
  const openPeriod = useCallback(() => setState({}, { overlay: 'report-period' }), [setState]);
  useEffect(() => {
    const shift = (event: Event) => setState({ anchor: shiftReportPeriodAnchor(anchor, period, (event as CustomEvent<{ direction: -1 | 1 }>).detail.direction) });
    window.addEventListener('kazna:period-shift', shift);
    window.addEventListener('kazna:section-period', openPeriod);
    return () => { window.removeEventListener('kazna:period-shift', shift); window.removeEventListener('kazna:section-period', openPeriod); };
  }, [anchor, period, setState, openPeriod]);
  return <>
    <div className="activity-modes native-activity-modes" role="tablist" aria-label="Режим отчёта">
      {(['history', 'reports'] as const).map(mode => <button type="button" role="tab" key={mode} className={activity.mode === mode ? 'active' : ''} aria-selected={activity.mode === mode} onClick={() => setState({ mode }, { overlay: null })}>{mode === 'history' ? 'История' : 'Отчёты'}</button>)}
    </div>
    {activity.mode === 'history' ? <HistoryPage /> : <ReportsPage />}
    {params.get('overlay') === 'report-period' && <Dialog manageFocus variant="sheet" title="Период отчёта" onClose={() => setState({}, { overlay: null, replace: true })}>
      <div className="native-activity-period"><div className="sheet-period-navigation">
        <button type="button" aria-label="Предыдущий период" onClick={() => setState({ anchor: shiftReportPeriodAnchor(anchor, period, -1) })}><ChevronLeft size={22} /></button>
        <strong>{formatReportPeriodTitle(anchor, period)}</strong>
        <button type="button" aria-label="Следующий период" onClick={() => setState({ anchor: shiftReportPeriodAnchor(anchor, period, 1) })}><ChevronRight size={22} /></button>
      </div>
      <div className="period-list">{([{ value: 'DAY', label: 'День' }, { value: 'WEEK', label: 'Неделя' }, { value: 'MONTH', label: 'Месяц' }, { value: 'YEAR', label: 'Год' }] as const).map(item => <button key={item.value} aria-label={`Период отчёта: ${item.label}`} aria-pressed={period === item.value} onClick={() => setState({ period: item.value }, { overlay: null, replace: true })}><span>{item.label}</span>{period === item.value && <Check size={20} />}</button>)}</div></div>
    </Dialog>}
  </>;
}
